import useSWR from 'swr';
import axios from 'axios';
import { EngineService } from '@/services/engine';
import { Event } from '@/types/event';
import { PaginatedResponse } from '@/types/responses';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { alertKey, mergeAlertEvents, useAlertsStore } from '@/stores';
import { getDefaultOrderBy } from '@/features/EventsListLayout/hooks/eventsDataShared';

interface UseRecentDataParams {
    page?: number;
    size?: number;
    orderBy?: string;
    classId?: number[];
}

const DEFAULT_ALERTS_PARAMS: UseRecentDataParams = {
    page: 1,
    size: 12,
    orderBy: getDefaultOrderBy(true),
};

const DEFAULT_EVENTS_PARAMS: UseRecentDataParams = {
    page: 1,
    size: 12,
    orderBy: getDefaultOrderBy(false),
};

const SWR_OPTIONS = {
    revalidateOnFocus: false,
    dedupingInterval: 30000,
} as const;

function createEmptyResponse(size: number): PaginatedResponse<never> {
    return {
        items: [],
        page: { totalItems: 0, page: 1, size, totalPages: 0 },
    };
}

export function useRecentAlerts() {
    const { recentAlerts, setAlerts } = useAlertsStore();

    const { isLoading } = useSWR(
        CACHE_KEYS.RECENT_ALERTS,
        async () => {
            const response = await EngineService.getAlertRuleEvents(
                DEFAULT_ALERTS_PARAMS,
            );
            const limit =
                DEFAULT_ALERTS_PARAMS.size ??
                response.page.size ??
                response.items.length;
            const merged = mergeAlertEvents(
                response.items,
                useAlertsStore.getState().recentAlerts,
                alertKey,
                limit,
            );
            setAlerts(merged);
            return response;
        },
        SWR_OPTIONS,
    );

    return {
        alerts: recentAlerts,
        isLoading: isLoading && recentAlerts.length === 0,
    };
}

export function useRecentEvents(params?: UseRecentDataParams) {
    const mergedParams = { ...DEFAULT_EVENTS_PARAMS, ...params };

    const { data, error, isLoading } = useSWR<PaginatedResponse<Event>>(
        [CACHE_KEYS.RECENT_EVENTS, mergedParams],
        async () => {
            try {
                return await EngineService.getEvents(mergedParams);
            } catch (err: unknown) {
                if (axios.isAxiosError(err) && err.response?.status === 404) {
                    return createEmptyResponse(
                        mergedParams.size || DEFAULT_EVENTS_PARAMS.size!,
                    ) as PaginatedResponse<Event>;
                }
                throw err;
            }
        },
        SWR_OPTIONS,
    );

    return { data, error, isLoading };
}
